import { FC, useState, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { isEmpty } from "lodash";
import __ from "src/utils/Translations";
import { IAbsenceTypeGroup } from "src/utils/Company/Absences/transformAbsenceTypesToGroupedLists";
import AbsenceTypesListItem from "./AbsenceTypesListItem";

interface IAbsenceTypesListProps {
  label: string;
  data: IAbsenceTypeGroup[];
  disabled: boolean;
}

const AbsenceTypesList: FC<IAbsenceTypesListProps> = ({
  label,
  data,
  disabled,
}) => {
  const initialExpandedState = useMemo(
    () => (data && data.length > 0 ? data.map((group) => group.groupId) : []),
    [data]
  );

  const [expandedAccordions, setExpandedAccordions] =
    useState<string[]>(initialExpandedState);

  if (!data || isEmpty(data)) return null;

  const toggleAll = () => {
    if (expandedAccordions.length === data.length) {
      setExpandedAccordions([]);
    } else {
      setExpandedAccordions(data.map((group) => group.groupId));
    }
  };

  const handleAccordionChange = (groupId: string) => {
    setExpandedAccordions((prev) => {
      if (prev.includes(groupId)) {
        return prev.filter((id) => id !== groupId);
      }
      return [...prev, groupId];
    });
  };

  const allExpanded = expandedAccordions.length === data.length;

  return (
    <Box sx={{ mb: "2rem" }}>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", mb: "1rem" }}
      >
        <Typography
          variant="subtitle1"
          component="h3"
          color="grey.700"
          sx={{ fontWeight: 700 }}
        >
          {__(label)}:
        </Typography>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          color="text.primary"
          component="button"
          sx={{ fontSize: "1rem" }}
          onClick={toggleAll}
        >
          {allExpanded ? __("Zwiń") : __("Rozwiń")} {__("wszystkie")}
        </Link>
      </Stack>
      {data.map((group) => (
        <Accordion
          key={group.groupId}
          variant="AbsenceGroupsAccordion"
          expanded={expandedAccordions.includes(group.groupId)}
          onChange={() => handleAccordionChange(group.groupId)}
          disableGutters
          aria-controls={`${group.groupId}-content`}
          id={`${group.groupId}-header`}
        >
          <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
            <Typography variant="h6" component="h4">
              {group.groupName}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {group.absenceTypes.map((item) => (
              <AbsenceTypesListItem
                key={item.id}
                item={item}
                disabled={disabled}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default AbsenceTypesList;
